/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import DashboardShell from "@/components/dashboard/DashboardShell";
import HotLeadsBanner from "@/components/dashboard/HotLeadsBanner";
import { StatusPill } from "@/pages/dashboard/TrainerHome";
import { Button } from "@/components/ui/button";
import {
  Users,
  CheckCircle2,
  Clock,
  Ban,
  ArrowRight,
  TrendingUp,
  Crown,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PLAN_PRICE = { essentiel: 5, pro: 15, domaine: 0 };

function StatCard({ label, value, icon: Icon, accent, sub }) {
  return (
    <div className="rounded-2xl bg-white border border-[#E2E4DF] p-6">
      <div className="flex items-center gap-4">
        <div
          className="w-12 h-12 rounded-xl grid place-items-center shrink-0"
          style={{ background: accent + "22", color: accent }}
        >
          <Icon size={22} />
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-[#5C6B5C] font-semibold">
            {label}
          </div>
          <div className="font-display text-3xl font-black text-[#1C3A27] mt-0.5">
            {value}
          </div>
        </div>
      </div>
      {sub && (
        <div className="text-xs text-[#5C6B5C] mt-3 border-t border-[#E2E4DF] pt-3">
          {sub}
        </div>
      )}
    </div>
  );
}

export default function OwnerHome() {
  const { user } = useAuth();
  const { t } = useLang();
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/admin/stats`, { withCredentials: true }),
      axios.get(`${API}/admin/clients`, { withCredentials: true }),
    ])
      .then(([s, c]) => {
        setStats(s.data);
        setClients(c.data);
      })
      .catch(() => {});
  }, []);

  const monthlyRevenue = clients.reduce((sum, c) => {
    if (c.status !== "active") return sum;
    const base = PLAN_PRICE[c.plan] || 0;
    const monthlyEq = c.billing_cycle === "annual" ? base * 0.8 : base;
    return sum + monthlyEq;
  }, 0);
  const annualRevenue = monthlyRevenue * 12;
  const trainerCommission = monthlyRevenue * 0.5;
  const netOwner = monthlyRevenue - trainerCommission;

  return (
    <DashboardShell allowedRoles={["owner"]}>
      <header className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.2em] text-[#E05A3D]">
            <Crown size={14} /> {t.app.owner.overline}
          </div>
          <h1 className="mt-2 font-display text-3xl md:text-4xl font-black text-[#1C3A27]">
            {t.app.owner.welcome} {user?.name?.split(" ")[0]} 👋
          </h1>
          <p className="mt-1 text-[#5C6B5C]">{t.app.owner.subtitle}</p>
        </div>
        <Link to="/dashboard/owner/clients">
          <Button
            data-testid="cta-view-clients"
            className="h-12 px-5 rounded-xl bg-[#E05A3D] hover:bg-[#C84B31] text-white font-semibold"
          >
            {t.app.owner.view_all_clients}
            <ArrowRight size={18} className="ms-2" />
          </Button>
        </Link>
      </header>

      <HotLeadsBanner />

      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
        <StatCard label={t.app.owner.stat_total} value={stats?.total ?? "—"} icon={Users} accent="#1C3A27" />
        <StatCard label={t.app.owner.stat_active} value={stats?.active ?? "—"} icon={CheckCircle2} accent="#1C7A3C" />
        <StatCard label={t.app.owner.stat_pending} value={stats?.pending ?? "—"} icon={Clock} accent="#E05A3D" />
        <StatCard label={t.app.owner.stat_suspended} value={stats?.suspended ?? "—"} icon={Ban} accent="#8A8A8A" />
      </section>

      <section className="grid lg:grid-cols-3 gap-5 mb-10">
        <div className="lg:col-span-2 rounded-3xl bg-[#1C3A27] text-white p-8 md:p-10 relative overflow-hidden">
          <div aria-hidden className="absolute -bottom-20 -right-20 w-[320px] h-[320px] rounded-full opacity-[0.06] bg-[radial-gradient(#FAFAF7_1px,transparent_1px)] [background-size:14px_14px]" />
          <div className="relative">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-white/60 font-bold">
              <TrendingUp size={14} /> {t.app.owner.mrr}
            </div>
            <div className="font-display text-5xl md:text-6xl font-black mt-3">
              {monthlyRevenue.toFixed(0)} DT
              <span className="text-xl text-white/60 ms-2 font-normal">{t.app.owner.per_month}</span>
            </div>
            <div className="mt-1 text-white/60 text-sm">
              {t.app.owner.annual_estimate.replace("{n}", annualRevenue.toFixed(0))}
            </div>
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
                <div className="text-[10px] uppercase tracking-widest text-white/60 font-bold">
                  {t.app.owner.commission_said}
                </div>
                <div className="font-display text-3xl font-black mt-1 text-[#E05A3D]">
                  {trainerCommission.toFixed(0)} DT
                </div>
              </div>
              <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
                <div className="text-[10px] uppercase tracking-widest text-white/60 font-bold">
                  {t.app.owner.net_owner}
                </div>
                <div className="font-display text-3xl font-black mt-1">
                  {netOwner.toFixed(0)} DT
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-3xl bg-white border border-[#E2E4DF] p-8">
          <div className="text-xs uppercase tracking-widest text-[#5C6B5C] font-semibold">
            {t.app.owner.plan_breakdown}
          </div>
          <ul className="mt-4 space-y-3">
            {["essentiel", "pro", "domaine"].map((p) => {
              const count = clients.filter((c) => c.plan === p && c.status === "active").length;
              const pct = stats?.active ? Math.round((count / stats.active) * 100) : 0;
              return (
                <li key={p}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="capitalize font-medium text-[#1C3A27]">{p}</span>
                    <span className="text-[#5C6B5C]">{count} ({pct}%)</span>
                  </div>
                  <div className="mt-1.5 h-2 rounded-full bg-[#F0F0EB] overflow-hidden">
                    <div className="h-full bg-[#E05A3D]" style={{ width: `${pct}%` }} />
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      <section>
        <div className="flex items-end justify-between mb-5">
          <h2 className="font-display text-xl md:text-2xl font-bold text-[#1C3A27]">
            {t.app.owner.recent_title}
          </h2>
          <Link to="/dashboard/owner/clients" className="text-sm font-semibold text-[#1C3A27] hover:text-[#E05A3D]">
            {t.app.common.view_all} →
          </Link>
        </div>
        <div className="rounded-2xl border border-[#E2E4DF] bg-white overflow-hidden">
          {clients.length === 0 ? (
            <div className="p-8 text-center text-[#5C6B5C]">{t.app.owner.no_clients}</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[#F0F0EB] text-[#5C6B5C] text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-start px-5 py-3 font-semibold">{t.app.clients_list.th_name}</th>
                  <th className="text-start px-5 py-3 font-semibold">{t.app.clients_list.th_email}</th>
                  <th className="text-start px-5 py-3 font-semibold">{t.app.clients_list.th_plan}</th>
                  <th className="text-start px-5 py-3 font-semibold">{t.app.clients_list.th_status}</th>
                </tr>
              </thead>
              <tbody>
                {clients.slice(0, 5).map((c) => (
                  <tr key={c.id} className="border-t border-[#E2E4DF]">
                    <td className="px-5 py-4 font-medium text-[#1C3A27]">{c.name}</td>
                    <td className="px-5 py-4 text-[#5C6B5C]">{c.email}</td>
                    <td className="px-5 py-4 capitalize">{c.plan}</td>
                    <td className="px-5 py-4"><StatusPill status={c.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </DashboardShell>
  );
}
